import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { decodedJWTPayload } from "../../utils/commonUtil";

import EditIcon from "../../static/image/editIcon.svg";
import DeleteIcon from "../../static/image/deleteIcon.svg";
import ProductDetailEdit from "./ProductDetailEdit";
import CommonDeleteModal from "../../components/commonDeleteModal/CommonDeleteModal";

import {
  API_STATE,
  FORM_TYPE,
  LANGUAGE_DEFAULT_OPTION,
  LANGUAGE_OPTIONS,
  STATUS_CODE_RESPONSE_OK,
  PERMISSION_VALUES,
  ROUTES
} from "../../utils/constant";

import { deleteProducts, fetchProducts } from "../../redux/productsSlice";
import CommonSuccessModal from "../../components/commonSuccessModal/CommonSuccessModal";
import { isEmpty } from "../../utils/commonUtil";

function ManageProduct() {
  const navigate = useNavigate();
  const { productId } = useParams();
  const location = useLocation();

  const dispatch = useDispatch();

  const {
    data: productList,
    status,
    error,
  } = useSelector((state) => state.productList);

  const [language, setLanguage] = useState(LANGUAGE_DEFAULT_OPTION);
  const [deleteModalShow, setDeleteModalShow] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [isShowAlert, setIsShowAlert] = useState(null);
  const [isAlertSuccess, setIAlertSuccess] = useState(null);
  const [permission, setPermission] = useState('');

  const formType = useRef(null);

  const getEditModeFromRoute = () => {
    if (location.pathname.includes('/create')) return FORM_TYPE.CREATE;
    if (location.pathname.includes('/edit/')) return FORM_TYPE.EDIT;
    return undefined;
  };

  const [editMode, setEditMode] = useState(getEditModeFromRoute());

  useEffect(() => {
    dispatch(fetchProducts(language));
    const token = localStorage.getItem('access_token');
    if (token) {
      const payload = decodedJWTPayload(token);
      if (payload?.mn_id) {
        const modulePermission = payload.mn_id.split('|')
          .find(item => item.startsWith('020200-'));
        if (modulePermission) {
          setPermission(modulePermission.split('-')[1]);
        }
      }
    }
  }, [dispatch, language]);

  useEffect(() => {
    const newEditMode = getEditModeFromRoute();
    setEditMode(newEditMode);
    formType.current = newEditMode;
  }, [location.pathname]);

  const toggleLanguage = (selectedLanguage) => {
    setLanguage(selectedLanguage);
    dispatch(fetchProducts(selectedLanguage));
  };

  const handleEdit = (id) => {
    if ([PERMISSION_VALUES.FULL_ACCESS, PERMISSION_VALUES.WRITE].includes(permission)) {
      navigate(`/dashboard/manage-product/edit/${id}`);
    }
  };

  const handleAddNew = () => {
    if (permission === PERMISSION_VALUES.FULL_ACCESS) {
      navigate(ROUTES.PRODUCT_CREATE);
    }
  };

  const handleResetProducts = () => {
    navigate(ROUTES.MANAGE_PRODUCT);
    dispatch(fetchProducts(language));
  };

  const handleBack = () => {
    navigate(ROUTES.MANAGE_PRODUCT);
  };

  const handleDeleteClick = (product) => {
    if (permission === PERMISSION_VALUES.FULL_ACCESS) {
      setProductToDelete(product);
      setDeleteModalShow(true);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalShow(false);
    setProductToDelete(null);
  };

  const handleDeleteConfirm = () => {
    if (productToDelete && permission === PERMISSION_VALUES.FULL_ACCESS) {
      const response = dispatch(deleteProducts(productToDelete.uuid));
      response
        .then((res) => {
          if (res?.payload?.status === STATUS_CODE_RESPONSE_OK) {
            dispatch(fetchProducts(language));
          }
          if (res?.error) {
            handleAlertShow(res.error.message, false);
          }

          setDeleteModalShow(false);
          setProductToDelete(null);

          if (editMode) {
            navigate(ROUTES.MANAGE_PRODUCT);
          }
        })
        .catch((err) => {
          handleAlertShow(err, false);
          setDeleteModalShow(false);
          setProductToDelete(null);
        });
    }
  };

  const handleAlertShow = (message, isSuccess) => {
    setIsShowAlert(message);
    setIAlertSuccess(isSuccess);
  };

  const handleAlertClose = () => {
    setIsShowAlert(null);
    setIAlertSuccess(null);
  };

  if (editMode) {
    return (
      <ProductDetailEdit
        productId={productId}
        formType={formType.current}
        initLanguage={language}
        handleComplete={handleResetProducts}
        handleBack={handleBack}
      />
    );
  }

  return (
    <div className="create-user-container">
      <div className="package-wrapper">
        <div
          className="package-language-container"
          style={{ position: "relative", top: "20px", display: "none" }}
        >
          <div className="package-language-toggle">
            {LANGUAGE_OPTIONS.map((option) => (
              <button
                key={option.id}
                onClick={() => toggleLanguage(option.value)}
                className={`package-language-option ${language === option.value ? "package-language-active" : ""
                  }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div className="package-container">
          <div className="package-header">Product</div>

          {status === API_STATE.LOADING && (
            <div className="loading-packages">Loading products...</div>
          )}

          {status === API_STATE.FAILED && (
            <div className="error-packages">
              Error loading products: {error}
            </div>
          )}

          {status === API_STATE.SUCCEEDED && (
            <>
              {productList.map((product, index) => {
                const basicInfo = product.contentJson?.basicInfo || {};
                const productName = basicInfo.name;
                const productPrice = basicInfo.price;

                return (
                  <div key={product.uuid} className={`package-item`}>
                    <div className="package-content">
                      <div className="package-info">
                        <div className="package-label">
                          Product name:&nbsp;
                          <span className="package-value">{productName}</span>
                        </div>
                        <div className="package-label">
                          Product Price:&nbsp;
                          <span className="package-value">{productPrice}</span>
                        </div>
                      </div>
                      <div className="package-actions">
                        <button
                          className="icon-btn"
                          onClick={() => handleEdit(product.uuid)}
                          style={{
                            opacity: [PERMISSION_VALUES.FULL_ACCESS, PERMISSION_VALUES.WRITE].includes(permission) ? 1 : 0.5,
                            cursor: [PERMISSION_VALUES.FULL_ACCESS, PERMISSION_VALUES.WRITE].includes(permission) ? 'pointer' : 'default',
                            pointerEvents: [PERMISSION_VALUES.FULL_ACCESS, PERMISSION_VALUES.WRITE].includes(permission) ? 'auto' : 'none'
                          }}
                        >
                          <img
                            src={EditIcon}
                            alt="Edit"
                            className="action-icon"
                          />
                        </button>
                        <button
                          className="icon-btn"
                          onClick={() => handleDeleteClick(product)}
                          style={{
                            opacity: permission === PERMISSION_VALUES.FULL_ACCESS ? 1 : 0.5,
                            cursor: permission === PERMISSION_VALUES.FULL_ACCESS ? 'pointer' : 'default',
                            pointerEvents: permission === PERMISSION_VALUES.FULL_ACCESS ? 'auto' : 'none'
                          }}
                        >
                          {language === "EN" && <img
                            src={DeleteIcon}
                            alt="Delete"
                            className="action-icon"
                          />}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}

              {productList.length === 0 && (
                <div className="empty-packages">
                  No products found. {permission === PERMISSION_VALUES.FULL_ACCESS && 'Click "Add new" to create your first product.'}
                </div>
              )}
            </>
          )}
        </div>

        {language === "EN" && (
          <button
            className="package-add-btn"
            onClick={handleAddNew}
            style={{
              opacity: permission === PERMISSION_VALUES.FULL_ACCESS ? 1 : 0.5,
              cursor: permission === PERMISSION_VALUES.FULL_ACCESS ? 'pointer' : 'default',
              pointerEvents: permission === PERMISSION_VALUES.FULL_ACCESS ? 'auto' : 'none'
            }}
          >
            Add new
          </button>
        )}
      </div>

      <CommonSuccessModal
        showModal={!isEmpty(isShowAlert)}
        handleClose={handleAlertClose}
        message={isShowAlert}
        status={isAlertSuccess}
      />

      <CommonDeleteModal
        show={deleteModalShow}
        onHide={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Product"
        instruction="Are you sure you want to delete this product?"
      />
    </div>
  );
}

export default ManageProduct;
